// Constructor de páginas del informe (prototipo portado del artifact).
// Funciones PURAS que generan el HTML esquemático de la vista previa A4. No dependen
// de Vue ni de `this`: reciben la página y un contexto { pages, groups, project }.
// OJO: solo maqueta/mockup con datos de ejemplo; aún sin persistencia.

export const DEFAULT_GROUPS = ['Obra civil', 'Mecánica', 'Eléctrica', 'SET', 'BESS', 'HV Substation']
export const DISCIPLINES = [['OC', 'Obra civil'], ['MM', 'Mecánica'], ['ME', 'Eléctrica'], ['LEV', 'Línea evacuación'], ['SET', 'SET'], ['BESS', 'BESS']]
export const DATE_TYPES = [['scheduled', 'Scheduled'], ['current', 'Current'], ['real', 'Real']]
export const DISC_TITLE = { OC: 'CIVIL WORK', MM: 'MECHANICAL INSTALLATION', ME: 'ELECTRICAL INSTALLATION', LEV: 'EVACUATION LINE', SET: 'SUBSTATION', BESS: 'BESS INSTALLATION' }
const IMGS_PER_PAGE = 6
const GC_FIRST_WITH_BARS = 2
const GC_PER_SHEET = 3

// Catálogo de tipos de página (paleta "añadir"). Parámetros derivados de
// default-sections.js (REPORT_COVER, RAG_SUMMARY, LINE_SELECTOR, TASK_COLUMNS_SELECTOR,
// secciones GENERAL/disciplinas/GANTT).
export const TYPES = {
  cover: { name: 'Portada', icon: 'image', custom: 'admin', single: true, defaults: () => ({ title: null, subtitle: null, description: null, image_cover: { type: 'file', name: null, mimeType: null, url: null, file: null } }) },
  dashboard: { name: 'Dashboard', icon: 'chart-pie', custom: 'admin', single: true, defaults: () => ({ showGroupProgress: 'parent', customGroups: [], firstDatePairType: 'scheduled', secondDatePairType: 'current', showEndDateOnCompletion: false, showChart: true, showGeneral: true, showTable: true }) },
  exec: { name: 'Resumen ejecutivo', icon: 'bullseye', custom: 'admin', defaults: () => ({ showOverall: true, showByGroup: true, showMilestones: false }) },
  groupcharts: { name: 'Gráficos por grupo', icon: 'chart-line', custom: 'admin', single: true, defaults: () => ({ scheduled: true, current: true, lastPlanned: false }) },
  scurve: { name: 'Curva S', icon: 'chart-area', custom: 'admin', single: true, defaults: () => ({ scheduled: true, current: true, lastPlanned: false, legend: true, extraGroups: [], includeDashboardGroups: true, showBars: false }) },
  images: { name: 'Imágenes', icon: 'images', custom: 'user', defaults: () => ({ columns: 2, showCaption: true, numberOfExtraImages: 4, userCanDefineLegend: false }) },
  discipline: { name: 'Disciplina', icon: 'layer-group', custom: 'user', defaults: () => ({ discipline: 'MM', name: '', groupId: null, numberOfExtraImages: 2, userCanHide: true, imageSource: 'activity', userCanDefineLegend: true }) },
  tasktable: { name: 'Tabla de tareas', icon: 'table-cells', custom: 'admin', single: true, defaults: () => ({ schedule: true, latest: false, actual: true, currentUnit: true, scheduledPct: true, latestPct: false }) },
  gantt: { name: 'Gantt', icon: 'calendar', custom: 'user', single: true, defaults: () => ({ landscape: true, userCanChangeOrientation: false, userCanHide: true, numberOfImages: 1, showColorLegend: true }) },
}

export const DESCRIP = {
  cover: 'Primera página: logo/imagen, título, subtítulo y descripción del proyecto.',
  dashboard: 'Resumen de avance por grupos: gráfico programado vs real y tabla de indicadores.',
  exec: 'KPIs de alto nivel para dirección.',
  groupcharts: 'Un gráfico de avance por cada grupo de actividad.',
  scurve: 'Avance acumulado real vs planificado. Elige qué líneas mostrar.',
  images: 'Galería de imágenes / planos de obra (sección GENERAL).',
  discipline: 'Páginas de una disciplina (imágenes, tablas, curva S, pictogramas).',
  tasktable: 'Listado detallado de tareas: elige qué columnas de fechas y progreso incluir.',
  gantt: 'Cronograma del proyecto.',
}

export function discName(code) { const d = DISCIPLINES.find((x) => x[0] === code); return d ? d[1] : 'Disciplina' }
export function dateLabel(code) { const d = DATE_TYPES.find((x) => x[0] === code); return d ? d[1] : code }
// Valores demo estables por grupo (programado, ejecutado) — chart y tabla coherentes.
function grpVals(i) { const sch = [98, 91, 90, 78, 53, 43, 60]; const exe = [99, 88, 85, 70, 30, 24, 48]; return [sch[i % 7], exe[i % 7]] }

// ---------- Curva S (3 líneas: azul scheduled / naranja current / morado últ. prog.) ----------
function scvSVG(o) {
  o = o || {}
  let paths = ''
  if (o.lastPlanned) paths += '<path d="M4,98 C48,92 88,62 118,42 C148,24 176,13 196,7" fill="none" stroke="#8e44ad" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/>'
  if (o.scheduled) paths += '<path d="M4,100 C50,95 90,70 120,52 C150,34 178,22 196,14" fill="none" stroke="#2f6cb5" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/>'
  if (o.current) paths += '<path d="M4,102 C52,99 92,82 122,66 C152,50 178,38 196,29" fill="none" stroke="var(--xorange)" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round"/>'
  return '<svg class="scv" viewBox="0 0 200 110" preserveAspectRatio="none"><line x1="0" y1="27.5" x2="200" y2="27.5" stroke="#eceff4" stroke-width=".7"/><line x1="0" y1="55" x2="200" y2="55" stroke="#eceff4" stroke-width=".7"/><line x1="0" y1="82.5" x2="200" y2="82.5" stroke="#eceff4" stroke-width=".7"/>' + paths + '</svg>'
}
function scurveLegend(o) {
  o = o || {}
  const items = []
  if (o.scheduled) items.push('<span><span class="dot" style="background:#2f6cb5"></span>Scheduled</span>')
  if (o.current) items.push('<span><span class="dot" style="background:var(--xorange)"></span>Current</span>')
  if (o.lastPlanned) items.push('<span><span class="dot" style="background:#8e44ad"></span>Última prog.</span>')
  return items.length ? '<div class="legend">' + items.join('') + '</div>' : ''
}

// ---------- Grupos (árbol de actividades) ----------
function flattenGroups(nodes, acc) {
  acc = acc || []
  ;(nodes || []).forEach((n) => { acc.push(n); if (n.children) flattenGroups(n.children, acc) })
  return acc
}
function groupLabelById(tree, id) {
  const f = flattenGroups(tree).find((n) => String(n.id) === String(id))
  return f ? (f.label || f.name) : null
}
// Nombres de grupo a mostrar según el modo del dashboard (parent/children/custom).
function dashGroupNames(ctx, s) {
  const tree = ctx.activityGroups || []
  const mode = s.showGroupProgress
  if (!tree.length) {
    // Sin árbol real (preview sin grupos, p.ej. "cargar de otro proyecto"): usa nombres demo.
    // En custom los customGroups son IDs del proyecto origen (no resolubles aquí) → también
    // caen a demo para que el dashboard/gráficos se pinten estructuralmente.
    return ctx.groups && ctx.groups.length ? ctx.groups : DEFAULT_GROUPS
  }
  if (mode === 'custom') return (s.customGroups || []).map((id) => groupLabelById(tree, id)).filter(Boolean)
  if (mode === 'children') return flattenGroups(tree).filter((n) => n.parent_id).map((n) => n.label || n.name)
  return tree.filter((n) => !n.parent_id).map((n) => n.label || n.name) // parent
}

// Grupos efectivos del dashboard (para que "Gráficos por grupo" cuadre con lo elegido allí).
function dashGroups(ctx) {
  const dp = ctx.pages.find((x) => x.type === 'dashboard')
  const fallback = ctx.groups && ctx.groups.length ? ctx.groups : DEFAULT_GROUPS
  if (!dp) return fallback
  const names = dashGroupNames(ctx, dp.settings || {})
  return names.length ? names : fallback
}

function gcBarsHtml(groups) {
  return '<div class="legend" style="justify-content:flex-start"><span><span class="dot" style="background:#5b86c4"></span>Group progress</span></div>' +
    '<div class="vbars">' + groups.map((n, i) => '<i style="height:' + grpVals(i)[0] + '%" title="' + n + '"></i>').join('') + '</div>' +
    '<div class="vbars-lbls">' + groups.map((n) => '<span>' + n + '</span>').join('') + '</div>'
}
function gcCurveHtml(name, lines) {
  return '<div class="sh-title" style="font-size:8.5px;margin:9px 0 3px">' + name.toUpperCase() + '</div>' + scvSVG(lines) + scurveLegend(lines)
}
// Reparte barras + curvas en varias hojas cuando no caben en un A4.
function gcSheets(p, ctx) {
  const s = p.settings
  const groups = dashGroups(ctx)
  const lines = { scheduled: s.scheduled, current: s.current, lastPlanned: s.lastPlanned }
  const sheets = []
  // La gráfica de barras por grupo es FIJA en "Gráficos por grupo" (siempre arriba).
  let cur = gcBarsHtml(groups), onSheet = 0, cap = GC_FIRST_WITH_BARS
  groups.forEach((n) => {
    if (onSheet >= cap) { sheets.push(cur); cur = ''; onSheet = 0; cap = GC_PER_SHEET }
    cur += gcCurveHtml(n, lines); onSheet++
  })
  if (cur) sheets.push(cur)
  if (!sheets.length) sheets.push(gcBarsHtml(groups))
  return sheets
}

// ---------- Curva S por grupo (grupos del dashboard en vivo + extras de la propia curva) ----------
const SC_PER_SHEET = 3
const SC_FIRST_WITH_BARS = 2
function scurveGroupNames(ctx, s) {
  const tree = ctx.activityGroups || []
  // Si existe la página "Gráficos por grupo", no repetimos aquí las curvas del dashboard.
  const hasGC = (ctx.pages || []).some((p) => p.type === 'groupcharts')
  const dash = (s.includeDashboardGroups === false || hasGC) ? [] : dashGroups(ctx) // heredados del dashboard
  const extra = (s.extraGroups || []).map((id) => groupLabelById(tree, id)).filter(Boolean)
  const seen = {}; const out = []
  dash.concat(extra).forEach((n) => { if (n && !seen[n]) { seen[n] = 1; out.push(n) } })
  return out
}
function scurveCurve(name, s, lines) {
  return '<div class="sh-title" style="font-size:8.5px;margin:9px 0 3px">' + name.toUpperCase() + '</div>' + scvSVG(lines) + (s.legend ? scurveLegend(lines) : '')
}
// Reparte barras (opcional) + curvas (una por grupo) en varias hojas A4.
function scurveSheets(p, ctx) {
  const s = p.settings
  const lines = { scheduled: s.scheduled, current: s.current, lastPlanned: s.lastPlanned }
  const names = scurveGroupNames(ctx, s)
  if (!names.length) return ['<div class="txl" style="color:var(--faint)">Sin grupos seleccionados.</div>']
  const sheets = []; let cur = ''; let n = 0; let cap = SC_PER_SHEET
  if (s.showBars) { cur = gcBarsHtml(names); cap = SC_FIRST_WITH_BARS } // barras arriba (como en gráficos por grupo)
  names.forEach((nm) => {
    if (n >= cap) { sheets.push(cur); cur = ''; n = 0; cap = SC_PER_SHEET }
    cur += scurveCurve(nm, s, lines); n++
  })
  if (cur) sheets.push(cur)
  return sheets
}

// ---------- Gantt ----------
function ganttHtml(landscape, legend) {
  const months = ['OCT', 'NOV', 'DIC', 'ENE', 'FEB', 'MAR', 'ABR', 'MAY']
  let tasks = [
    ['Engineering & Design', 2, 20, 'var(--xblue)'],
    ['Procurement', 14, 26, '#6aa9e9'],
    ['Civil Works', 24, 30, 'var(--xorange)'],
    ['Mechanical Install', 40, 34, '#86c06a'],
    ['Electrical Install', 52, 30, '#e0b84a'],
    ['SET / Substation', 60, 24, '#b07fd6'],
    ['Commissioning', 78, 18, 'var(--xgreen)'],
    ['Handover', 90, 8, 'var(--xred)'],
  ]
  if (landscape) {
    tasks = tasks.concat([
      ['BESS Installation', 56, 26, '#4ec9c0'],
      ['HV Line', 46, 34, '#d67fae'],
      ['Testing & Energization', 82, 14, '#8a93a6'],
      ['Punch List', 92, 6, '#f5a623'],
    ])
  }
  const head = '<div class="g-head"><div class="g-lbl"></div><div class="g-cols">' + months.map((m) => '<span>' + m + '</span>').join('') + '</div></div>'
  const rows = tasks.map((t) => '<div class="g-row"><div class="g-lbl">' + t[0] + '</div><div class="g-track"><b style="left:' + t[1] + '%;width:' + t[2] + '%;background:' + t[3] + '"></b></div></div>').join('')
  const leg = legend ? '<div class="legend" style="flex-wrap:wrap;margin-top:7px">' + tasks.map((t) => '<span><span class="dot" style="background:' + t[3] + '"></span>' + t[0] + '</span>').join('') + '</div>' : ''
  return '<div class="g-chart' + (landscape ? ' land' : '') + '">' + head + rows + '</div>' + leg
}

function imgPages(n) { return n > 0 ? Math.ceil(n / IMGS_PER_PAGE) : 0 }

function imagesSheetHtml(count, cols, caption, prefix) {
  if (count <= 0) return '<div class="txl" style="color:var(--faint)">sin imágenes</div>'
  cols = cols || 2
  let im = '<div class="imgs-big" style="grid-template-columns:repeat(' + cols + ',1fr)">'
  for (let j = 0; j < count; j++) im += '<div>' + (caption ? '<div class="imcap">' + (prefix || 'General') + '_' + (j + 1) + '.jpg</div>' : '') + '<div class="ph">imagen</div></div>'
  return im + '</div>'
}

function pictoLayerHtml(value) {
  const pic = '<div class="ph" style="height:150px;align-items:flex-start;justify-content:flex-start;padding:8px"><div style="display:flex;flex-wrap:wrap;gap:2px;align-content:flex-start">' +
    Array(96).fill(0).map(() => '<span style="width:7px;height:4px;border-radius:1px;display:block;background:var(--xgreen)"></span>').join('') + '</div></div>'
  const tbl = '<table class="sleg" style="margin-top:8px"><thead><tr><th>Color</th><th>Status</th><th>Value</th></tr></thead><tbody>' +
    [['#ffe600', 'In Progress', '0'], ['#22c923', 'Executed', value], ['#e8402a', 'Rejected', '0'], ['#2f6b2f', 'Released', '0']].map((r) => '<tr><td class="sw" style="background:' + r[0] + '"></td><td>' + r[1] + '</td><td>' + r[2] + '</td></tr>').join('') + '</tbody></table>'
  return pic + tbl
}

// ---------- Contenido de una página (una "hoja" base) ----------
export function sheetInner(p, ctx) {
  const s = p.settings
  const PROJECT = ctx.project
  switch (p.type) {
    case 'cover': {
      const ic = s.image_cover || {}
      // Prioridad: imagen recién elegida (objectURL en ctx) → imagen de BD (S3) → placeholder.
      const src = ctx.coverPreview || (ic.url ? '//' + ctx.s3bucket + '/' + ic.url : '')
      const img = src ? '<img src="' + src + '" alt="" style="width:100%;height:100%;object-fit:cover">' : 'imagen de portada'
      return '<div class="cov-co">' + (s.title || PROJECT.company.toUpperCase()) + '</div>' +
        '<div class="cov-r"><span class="cov-pj">' + PROJECT.name + '</span><span class="cov-wk">Week of the year:' + PROJECT.week + '</span></div>' +
        (s.subtitle ? '<div class="txl" style="margin-bottom:8px;font-size:9px"><b>' + s.subtitle + '</b></div>' : '') +
        (s.description ? '<div class="cov-dc">' + s.description + '</div>' : '') +
        '<div class="ph" style="height:300px;padding:0;overflow:hidden">' + img + '</div>'
    }

    case 'dashboard': {
      const meta = [['Project', PROJECT.name], ['Location', 'FM 3426, Paris TX'], ['Area (Ha)', '258 Ha'], ['Power AC/DC', 'AUI Partners'], ['Main Contractor', 'ONCOR'], ['Module Type', 'Power Electr.'], ['Module Power', 'USD'], ['Inverter Type', 'X-Elio Energy']]
      const md = '<div class="md">' + meta.map((m) => '<div><div class="k">' + m[0] + '</div><div class="v">' + m[1] + '</div></div>').join('') + '</div>'
      const rowsSrc = dashGroupNames(ctx, s)
      const rows = rowsSrc.length ? rowsSrc : ['—']
      const chartActual = s.showChart ? '<div class="ctr">Current Progress</div>' +
        '<div class="legend" style="margin:2px 0 4px"><span><span class="dot" style="background:#5b86c4"></span>Scheduled</span><span><span class="dot" style="background:var(--xorange)"></span>Executed</span></div>' +
        '<div class="hbars">' + rows.map((n, i) => { const v = grpVals(i); return '<div class="hrow"><div class="hl">' + n + '</div><div class="ht"><i class="bsch" style="width:' + v[0] + '%"></i><i class="bexe" style="width:' + v[1] + '%"></i></div></div>' }).join('') + '</div>' : ''
      const general = s.showGeneral ? '<div class="legend" style="margin-top:9px"><span><span class="dot" style="background:var(--xorange)"></span>Total Executed: 91.5%</span><span><span class="dot" style="background:#5b86c4"></span>Total Scheduled: 95.95%</span></div>' +
        '<div class="sumbar"><b class="bexe" style="width:91.5%"></b></div><div class="sumbar"><b class="bsch" style="width:95.95%"></b></div>' : ''
      const L1 = dateLabel(s.firstDatePairType), L2 = dateLabel(s.secondDatePairType)
      const tbl = s.showTable ? '<table class="ptbl sm" style="margin-top:9px"><thead><tr><th>Task</th><th>' + L1 + ' Start</th><th>' + L1 + ' End</th><th>' + L2 + ' Start</th><th>' + L2 + ' End</th><th>% Adv</th><th>% Project</th></tr></thead><tbody>' +
        rows.map((n, i) => { const v = grpVals(i); return '<tr><td>' + n + '</td><td>16-04-25</td><td>06-02-26</td><td>16-01-26</td><td>12-08-26</td><td>' + v[1] + ' %</td><td>' + v[0] + ' %</td></tr>' }).join('') + '</tbody></table>' : ''
      const modeLbl = s.showGroupProgress === 'parent' ? 'grupo padre' : (s.showGroupProgress === 'children' ? 'subgrupos' : 'grupos seleccionados')
      const none = (!s.showChart && !s.showGeneral && !s.showTable) ? '<div class="ph" style="height:50px">sin contenido</div>' : ''
      return md + '<div class="txl" style="font-size:7px">Progreso por ' + modeLbl + '</div>' + chartActual + general + tbl + none
    }

    case 'groupcharts':
      return gcSheets(p, ctx).join('')

    case 'exec':
      return (s.showOverall ? '<div class="txl"><b>Avance global:</b> 42.1% · <b>Previsto:</b> 47.8%</div>' : '') +
        (s.showByGroup ? '<table class="ptbl" style="margin-top:5px"><thead><tr><th>Grupo</th><th>%</th></tr></thead><tbody><tr><td>Mecánica</td><td>42</td></tr><tr><td>Eléctrica</td><td>31</td></tr></tbody></table>' : '') +
        (s.showMilestones ? '<div class="txl" style="margin-top:6px"><b>Hitos:</b> 3 próximos</div>' : '')

    case 'scurve':
      // Una curva S por grupo (dashboard + extras); la paginación real la hace scurveSheets().
      return scurveSheets(p, ctx).join('')

    case 'images': {
      const c = s.columns || 2
      const n = Math.max(0, s.numberOfExtraImages || 0)
      if (n === 0) return '<div class="txl" style="color:var(--faint)">sin imágenes</div>'
      let im = '<div class="imgs" style="grid-template-columns:repeat(' + c + ',1fr)">'
      for (let j = 0; j < Math.min(n, 9); j++) im += '<div>' + (s.showCaption ? '<div class="imcap">General_Overhead ' + (j + 1) + '.jpg</div>' : '') + '<div class="ph">imagen</div></div>'
      return im + '</div>'
    }

    case 'discipline': {
      // Grupo vinculado (si hay) encabezando la sección.
      const gName = s.groupId ? groupLabelById(ctx.activityGroups || [], s.groupId) : null
      const grpHdr = gName ? '<div class="txl" style="font-size:7.5px;margin-bottom:5px"><b>Grupo:</b> ' + gName + '</div>' : ''
      const scurveBlock = '<div style="margin-top:8px">' + scvSVG({ scheduled: true, current: true }) + '</div>' + scurveLegend({ scheduled: true, current: true })
      if (s.discipline === 'MM') {
        const blocks = ['var(--xpink)', 'var(--xgreen)', 'var(--xpink)', 'var(--xred)', 'var(--xgreen)']
        const pic = '<div class="ph" style="height:104px;align-items:flex-start;justify-content:flex-start;padding:6px"><div style="display:flex;flex-wrap:wrap;gap:2px;align-content:flex-start">' +
          Array(44).fill(0).map((_, k) => '<span style="width:7px;height:4px;border-radius:1px;display:block;background:' + blocks[k % blocks.length] + '"></span>').join('') + '</div></div>'
        const pp = '<div><div class="sh-title" style="font-size:8.5px;margin:0 0 3px">Project progress 70,22 %</div>' +
          '<table class="ptbl sm"><thead><tr><th>Task</th><th>Weekly</th><th>Progress</th></tr></thead><tbody>' +
          [['POWER BLOCK 21A1', '14.07 %', '47.4 %'], ['POWER BLOCK 21A2', '6.99 %', '73.65 %'], ['POWER BLOCK 22A5', '22.09 %', '55.42 %'], ['POWER BLOCK 22A6', '33.33 %', '66.66 %'], ['POWER BLOCK 23A1', '0 %', '33.33 %']].map((r) => '<tr><td>' + r[0] + '</td><td>' + r[1] + '</td><td>' + r[2] + '</td></tr>').join('') +
          '</tbody></table></div>'
        const totals = '<table class="ptbl sm" style="margin-top:8px"><thead><tr><th>Type</th><th>Total</th><th>Executed</th><th>Schedule</th><th>% Current</th><th>% Schedule</th></tr></thead><tbody>' +
          [['Pilings', '37492', '37492', '31061', '100,00', '82,85'], ['Structures', '2911', '2126', '2045', '73,03', '70,28'], ['Modules', '272720', '107464', '137830', '39,40', '50,54']].map((r) => '<tr><td>' + r[0] + '</td><td>' + r[1] + '</td><td>' + r[2] + '</td><td>' + r[3] + '</td><td>' + r[4] + '</td><td>' + r[5] + '</td></tr>').join('') + '</tbody></table>'
        // MM: imagen (pictograma) + tabla SIEMPRE (también con "Ninguna"). Con Actividad/Plano añade leyenda.
        const mmTop = '<div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;align-items:start">' + pic + pp + '</div>'
        const mmLeg = s.imageSource !== 'none'
          ? '<div style="margin-top:8px"><div class="sh-title" style="font-size:8.5px;margin:0 0 3px">Status legend</div><table class="sleg"><thead><tr><th>Color</th><th>Status</th></tr></thead><tbody><tr><td class="sw" style="background:var(--xgreen)"></td><td>Executed</td></tr></tbody></table></div>'
          : ''
        return grpHdr + mmTop + totals + mmLeg + scurveBlock
      }
      if (s.discipline === 'LEV') {
        const LEV_SECS = [
          { name: 'Privativa ap.02-ap.10', diag: true, tasks: [['Excavation', '#f5a623', 9], ['Foundation', '#2aa3e0', 9], ['Lifting', '#22c923', 9], ['Laying cables', 'bar', 10]] },
          { name: 'Privativa ap.11-ap.18', diag: false, tasks: [['Excavation', '#f5a623', 8], ['Foundation', '#2aa3e0', 8], ['Lifting', '#22c923', 8], ['Laying cables', 'bar', 9]] },
          { name: 'Ap.18 a Ap.31', diag: false, tasks: [['Excavation', '#f5a623', 1], ['Foundation', '#2aa3e0', 3], ['Lifting', '#22c923', 12], ['Laying cables', 'bar', 0]] },
        ]
        const levHtml = '<div style="display:flex;flex-direction:column;gap:11px">' + LEV_SECS.map((sec) => {
          const diag = sec.diag ? '<div class="lev-diag">' + Array(8).fill(0).map((_, i) => '<span class="lev-pt" style="background:' + (i >= 6 ? '#2aa3e0' : '#22c923') + '"></span>').join('') + '</div>' : ''
          const rows = sec.tasks.map((t) => {
            const marker = t[1] === 'bar' ? '<span class="lev-dot bar" style="background:#22c923"></span>' : '<span class="lev-dot" style="background:' + t[1] + '"></span>'
            return '<div class="lev-task">' + marker + '<div class="lev-box"><span>' + t[0] + '</span><span class="lev-num">' + t[2] + '</span></div></div>'
          }).join('')
          return '<div><div class="txl" style="font-weight:700;color:var(--ink);margin-bottom:4px">' + sec.name + '</div>' + diag + '<div style="display:flex;flex-direction:column;gap:4px">' + rows + '</div></div>'
        }).join('') + '</div>'
        return grpHdr + levHtml
      }
      // Imagen según imageSource; leyenda de estados según userCanDefineLegend (no-MM).
      const imgPart = s.imageSource !== 'none' ? '<div class="ph" style="height:78px">plano / pictograma</div>' : ''
      const legPart = s.userCanDefineLegend ? '<div><div class="sh-title" style="font-size:8.5px;margin:0 0 3px">Status legend</div><table class="sleg"><thead><tr><th>Color</th><th>Status</th></tr></thead><tbody><tr><td class="sw" style="background:var(--xgreen)"></td><td>Fence Install</td></tr></tbody></table></div>' : ''
      let std2Top = ''
      if (imgPart && legPart) std2Top = '<div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;align-items:start">' + imgPart + legPart + '</div>'
      else if (imgPart || legPart) std2Top = imgPart || legPart
      return grpHdr + std2Top +
        '<div class="sh-title" style="font-size:9px;margin:8px 0 3px">Construction Progress</div>' +
        '<table class="ptbl sm"><thead><tr><th>Initial Task</th><th>Weekly</th><th>Acumulated</th></tr></thead><tbody>' +
        [['Survey', '0,00', '100,00'], ['Clear & Grub', '0,00', '100,00'], ['Perimeter Fence', '0,00', '23,70']].map((r) => '<tr><td>' + r[0] + '</td><td>' + r[1] + '</td><td>' + r[2] + '</td></tr>').join('') + '</tbody></table>' +
        scurveBlock
    }

    case 'tasktable': {
      const cols = [{ h: 'Task', c: 'task' }, { h: 'Unit', c: 'unit' }, { h: 'BOQ', c: 'boq' }]
      if (s.schedule) { cols.push({ h: 'Sch. Start', c: 'dt' }); cols.push({ h: 'Sch. End', c: 'dt' }) }
      if (s.latest) { cols.push({ h: 'Latest Start', c: 'dt' }); cols.push({ h: 'Latest Finish', c: 'dt' }) }
      if (s.actual) { cols.push({ h: 'Actual Start', c: 'dt' }); cols.push({ h: 'Actual Finish', c: 'dt' }) }
      if (s.currentUnit) cols.push({ h: 'Current (unit)', c: 'cur' })
      cols.push({ h: 'Current %', c: 'curp' })
      if (s.scheduledPct) cols.push({ h: 'Project Sch. %', c: 'schp' })
      if (s.latestPct) cols.push({ h: 'Latest %', c: 'latp' })
      const rows = [
        ['Equipment Grounding', '1200', '42', '48', '45'],
        ['Install SCADA', '1', '88', '90', '86'],
        ['Install Fire Alarm', '1', '100', '100', '100'],
        ['BESS Cold Comm.', '1', '24', '30', '22'],
      ]
      const ttCell = (c, r) => {
        switch (c.c) {
          case 'task': return r[0]
          case 'unit': return 'ls'
          case 'boq': return r[1]
          case 'dt': return '12-03-27'
          case 'cur': return r[1]
          case 'curp': return r[2] + '%'
          case 'schp': return r[3] + '%'
          case 'latp': return r[4] + '%'
          default: return ''
        }
      }
      return '<table class="ptbl sm tt"><thead><tr>' + cols.map((c) => '<th class="' + c.c + '">' + c.h + '</th>').join('') + '</tr></thead><tbody>' +
        rows.map((r) => '<tr>' + cols.map((c) => '<td class="' + c.c + '">' + ttCell(c, r) + '</td>').join('') + '</tr>').join('') + '</tbody></table>'
    }

    case 'gantt': {
      const gTotal = Math.max(1, s.numberOfImages || 1)
      return ganttHtml(s.landscape, s.showColorLegend) +
        '<div class="txl" style="margin-top:6px">Orientación: ' + (s.landscape ? 'Horizontal' : 'Vertical') + (s.userCanChangeOrientation ? ' · usuario puede cambiar' : '') +
        ' · ' + gTotal + (gTotal > 1 ? ' imágenes, 1 por hoja' : ' imagen') + '</div>'
    }
  }
  return ''
}

export function sheetTitle(p) {
  if (p.type === 'images') return 'GENERAL'
  if (p.type === 'discipline') return DISC_TITLE[p.settings.discipline] || 'DISCIPLINA'
  if (p.type === 'gantt') return 'GANTT'
  if (p.type === 'exec') return 'RESUMEN EJECUTIVO'
  return ''
}

// Nº de hojas (páginas de salida) que genera una página del constructor.
export function sheetCount(p, ctx) {
  const s = p.settings
  if (p.type === 'images') return Math.max(1, imgPages(s.numberOfExtraImages || 0))
  if (p.type === 'groupcharts') return gcSheets(p, ctx).length
  if (p.type === 'scurve') return scurveSheets(p, ctx).length
  if (p.type === 'gantt') return Math.max(1, s.numberOfImages || 1)
  if (p.type !== 'discipline') return 1
  let n = 1 + imgPages(s.numberOfExtraImages || 0)
  if (s.discipline === 'MM') n += 3
  return n
}

// Hojas (con título + html) que genera una página; disciplina/imágenes/gantt se expanden.
export function sheetsFor(p, ctx) {
  const s = p.settings
  if (p.type === 'images') {
    const totG = s.numberOfExtraImages || 0, pgG = Math.max(1, imgPages(totG))
    const out = []
    for (let g = 0; g < pgG; g++) {
      out.push({ t: 'GENERAL' + (pgG > 1 ? ' (' + (g + 1) + '/' + pgG + ')' : ''), html: imagesSheetHtml(Math.min(IMGS_PER_PAGE, totG - g * IMGS_PER_PAGE), s.columns, s.showCaption, 'General_Overhead') })
    }
    return out
  }
  if (p.type === 'discipline') {
    const disc = DISC_TITLE[s.discipline] || 'DISCIPLINA'
    const arr = [{ t: disc, html: sheetInner(p, ctx) }]
    const n = s.numberOfExtraImages || 0, pg = imgPages(n)
    for (let k = 0; k < pg; k++) {
      arr.push({ t: disc + ' — imágenes' + (pg > 1 ? ' (' + (k + 1) + '/' + pg + ')' : ''), html: imagesSheetHtml(Math.min(IMGS_PER_PAGE, n - k * IMGS_PER_PAGE), 2, true, s.discipline) })
    }
    if (s.discipline === 'MM') {
      [['Pilings', '37492'], ['Structures', '2126'], ['Modules', '107464']].forEach((l) => {
        arr.push({ t: 'PVP Plant ' + l[0], html: pictoLayerHtml(l[1]) })
      })
    }
    return arr
  }
  if (p.type === 'groupcharts') {
    const gs = gcSheets(p, ctx), base = sheetTitle(p)
    return gs.map((html, i) => ({ t: base + (gs.length > 1 ? ' (' + (i + 1) + '/' + gs.length + ')' : ''), html }))
  }
  if (p.type === 'scurve') {
    const ss = scurveSheets(p, ctx)
    return ss.map((html, i) => ({ t: 'CURVA S' + (ss.length > 1 ? ' (' + (i + 1) + '/' + ss.length + ')' : ''), html }))
  }
  if (p.type === 'gantt') {
    const gTot = Math.max(1, s.numberOfImages || 1)
    const gSuffix = (i) => (gTot > 1 ? ' — imagen ' + i + '/' + gTot : '')
    const gout = [{ t: sheetTitle(p) + gSuffix(1), land: s.landscape, html: sheetInner(p, ctx) }]
    for (let gi = 2; gi <= gTot; gi++) {
      gout.push({ t: sheetTitle(p) + gSuffix(gi), land: s.landscape, html: ganttHtml(s.landscape, s.showColorLegend) })
    }
    return gout
  }
  return [{ t: sheetTitle(p), land: false, html: sheetInner(p, ctx) }]
}

function headHtml(ctx) {
  return '<div class="sh-head"><div><div class="sh-report">Report</div><div class="sh-proj">' + ctx.project.name + '</div></div><div class="sh-logo">X-ELIO</div></div>'
}

export function totalSheets(ctx) { return ctx.pages.reduce((a, pg) => a + sheetCount(pg, ctx), 0) }

function oneSheet(p, sh, num, total, si, ctx) {
  const isCover = p.type === 'cover'
  const head = isCover ? '' : headHtml(ctx)
  const title = sh.t ? '<div class="sh-title">' + sh.t + '</div>' : ''
  const foot = isCover ? '' : '<div class="sh-foot">' + num + '/' + total + '</div>'
  return '<div class="sheet' + (sh.land ? ' landscape' : '') + '" data-pid="' + p.id + '" data-si="' + si + '">' + head + title + sh.html + foot + '</div>'
}

// HTML completo de la vista previa (una página seleccionada o el documento completo).
export function buildPreview(ctx, mode, selectedId) {
  if (mode === 'full') {
    const vps = ctx.pages.filter((x) => x.visible !== false)
    const totalF = vps.reduce((a, pg) => a + sheetCount(pg, ctx), 0)
    let num = 0
    const parts = []
    vps.forEach((pg) => { sheetsFor(pg, ctx).forEach((sh, si) => { num++; parts.push(oneSheet(pg, sh, num, totalF, si, ctx)) }) })
    return parts.length ? parts.join('<div style="height:16px"></div>') : '<div class="empty-center">No hay páginas visibles.</div>'
  }
  const p = ctx.pages.find((x) => x.id === selectedId)
  if (!p) return ''
  let offset = 0
  const pi = ctx.pages.indexOf(p)
  for (let k = 0; k < pi; k++) offset += sheetCount(ctx.pages[k], ctx)
  const tot = totalSheets(ctx)
  return sheetsFor(p, ctx).map((sh, si) => oneSheet(p, sh, offset + si + 1, tot, si, ctx)).join('<div style="height:16px"></div>')
}
